import React, { useEffect, useMemo, useState } from "react";
import { Tabs, Tab, Badge, Table, Button, Form, Modal, Spinner } from "react-bootstrap";
import api from "../../api";
import Can from "../../components/Can";
import ProjectMemberPicker from "../../components/ProjectMemberPicker";
import ProjectModuleManagement from "./ProjectModuleManagement";
import ModuleMembersManagement from "./ModuleMembersManagement";
import { usePermissions } from "../../context/PermissionContext";
import { toast } from "react-toastify";
import ConfirmModal from "../../components/common/ConfirmModal";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import "./ProjectDetailPage.css";

const MEMBERSHIP_TYPES = ["OWNER", "MANAGER", "MEMBER", "REVIEWER", "TESTER", "OBSERVER"];
const VALID_TABS = new Set(["overview", "members", "modules", "module-members"]);

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const statusColor = (status) => {
  switch (status) {
    case "ACTIVE": return "success";
    case "PLANNED": return "info";
    case "ON_HOLD": return "warning";
    case "COMPLETED": return "primary";
    case "CANCELLED": return "danger";
    case "ARCHIVED": return "secondary";
    default: return "secondary";
  }
};

/**
 * Shared project-detail body (mini summary + Overview/Members/Modules/Module
 * Members tabs + Change Owner modal + remove-member confirm). Extracted so
 * the same logic renders identically whether it's hosted inside
 * ProjectDetailPage's own /admin/projects/:id route (standalone, deep-link
 * friendly) or expanded inline under a row on the Project Management list
 * (onCollapse provided). Tab selection is "controlled" only when the caller
 * passes activeTab+onTabChange (that's what lets ProjectDetailPage keep its
 * existing ?tab= deep-linking) -- otherwise the panel tracks its own tab
 * state internally, seeded from initialTab, which is all the inline list
 * usage needs.
 */
export default function ProjectDetailPanel({
  projectId,
  activeTab: controlledActiveTab,
  onTabChange,
  initialTab = "overview",
  onCollapse,
  onProjectChanged,
}) {
  const { can } = usePermissions();

  const [uncontrolledTab, setUncontrolledTab] = useState(
    VALID_TABS.has(initialTab) ? initialTab : "overview"
  );
  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : uncontrolledTab;
  const handleSelectTab = (key) => {
    if (onTabChange) onTabChange(key);
    else setUncontrolledTab(key);
  };

  const [project, setProject] = useState(null);
  const [loadingProject, setLoadingProject] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  const [newMemberUserId, setNewMemberUserId] = useState("");
  const [newMemberType, setNewMemberType] = useState("MEMBER");
  const [newMemberIsManager, setNewMemberIsManager] = useState(false);
  const [newMemberCanManageMembers, setNewMemberCanManageMembers] = useState(false);
  const [newMemberCanManageModules, setNewMemberCanManageModules] = useState(false);

  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [removeMemberTarget, setRemoveMemberTarget] = useState(null);
  const [removingMember, setRemovingMember] = useState(false);
  const [newOwnerUserId, setNewOwnerUserId] = useState("");

  useEffect(() => {
    fetchProject();
    fetchMembers();
    fetchAllUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const notifyProjectChanged = () => {
    if (onProjectChanged) onProjectChanged();
  };

  const fetchProject = async () => {
    try {
      setLoadingProject(true);
      setLoadError("");
      const res = await api.get(`/projects/${projectId}`);
      setProject(res.data?.data || null);
    } catch (err) {
      console.error("Fetch project error:", err);
      setLoadError(err.response?.data?.message || "Failed to load project");
    } finally {
      setLoadingProject(false);
    }
  };

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      const res = await api.get(`/projects/${projectId}/members`, { params: { pageSize: 200 } });
      setMembers(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("Fetch members error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch project members");
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await api.get("/users");
      setAllUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch users error:", err);
    }
  };

  // GET /users (already fetched for the member/owner pickers) already
  // returns email + role_name (see controllers/userController.js#getUsers) --
  // reused here purely on the frontend to enrich member rows instead of
  // adding an email/role JOIN to the project_members backend query.
  const usersById = useMemo(() => {
    const map = new Map();
    allUsers.forEach((u) => map.set(Number(u.id), u));
    return map;
  }, [allUsers]);

  const membersWithUserInfo = useMemo(
    () => members.map((m) => {
      const u = usersById.get(Number(m.user_id));
      return { ...m, email: u?.email || m.email, role_name: u?.role_name || m.role_name };
    }),
    [members, usersById]
  );

  const activeMembers = useMemo(() => membersWithUserInfo.filter((m) => m.is_active), [membersWithUserInfo]);
  const activeMemberUserIds = useMemo(() => activeMembers.map((m) => m.user_id), [activeMembers]);

  const resolveUserName = (id) => (id ? usersById.get(Number(id))?.name || `#${id}` : "—");

  const addMember = async () => {
    if (!newMemberUserId) return;
    try {
      await api.post(`/projects/${projectId}/members`, {
        user_id: newMemberUserId,
        membership_type: newMemberType,
        is_project_manager: newMemberIsManager,
        can_manage_members: newMemberCanManageMembers,
        can_manage_modules: newMemberCanManageModules,
      });
      toast.success("Project member added");
      setNewMemberUserId("");
      setNewMemberType("MEMBER");
      setNewMemberIsManager(false);
      setNewMemberCanManageMembers(false);
      setNewMemberCanManageModules(false);
      fetchMembers();
      fetchProject();
      notifyProjectChanged();
    } catch (err) {
      console.error("Add member error:", err);
      const data = err.response?.data;
      if (data?.code === "PROJECT_MEMBER_EXISTS") {
        toast.error("This user is already an active member of the project.");
      } else {
        toast.error(data?.message || "Failed to add project member");
      }
    }
  };

  const updateMembershipType = async (membership, membershipType) => {
    try {
      await api.put(`/projects/${projectId}/members/${membership.id}`, { membership_type: membershipType });
      toast.success("Membership updated");
      fetchMembers();
    } catch (err) {
      console.error("Update membership error:", err);
      toast.error(err.response?.data?.message || "Failed to update membership");
    }
  };

  const toggleMembershipFlag = async (membership, field) => {
    try {
      await api.put(`/projects/${projectId}/members/${membership.id}`, { [field]: !membership[field] });
      fetchMembers();
    } catch (err) {
      console.error("Update membership flag error:", err);
      toast.error(err.response?.data?.message || "Failed to update membership");
    }
  };

  const toggleMembershipStatus = async (membership) => {
    try {
      await api.patch(`/projects/${projectId}/members/${membership.id}/status`, { is_active: !membership.is_active });
      toast.success(membership.is_active ? "Membership deactivated" : "Membership reactivated");
      fetchMembers();
      fetchProject();
      notifyProjectChanged();
    } catch (err) {
      console.error("Toggle membership error:", err);
      toast.error(err.response?.data?.message || "Failed to update membership");
    }
  };

  const confirmRemoveMember = async () => {
    if (!removeMemberTarget) return;
    setRemovingMember(true);
    try {
      await api.delete(`/projects/${projectId}/members/${removeMemberTarget.id}`);
      toast.success("Project member removed");
      setRemoveMemberTarget(null);
      fetchMembers();
      fetchProject();
      notifyProjectChanged();
    } catch (err) {
      console.error("Remove member error:", err);
      toast.error(err.response?.data?.message || "Failed to remove project member");
    } finally {
      setRemovingMember(false);
    }
  };

  const openOwnerModal = () => {
    setNewOwnerUserId(project?.owner_user_id || "");
    setShowOwnerModal(true);
  };

  const changeOwner = async () => {
    if (!newOwnerUserId) {
      toast.warning("Select a new owner first");
      return;
    }
    try {
      await api.put(`/projects/${projectId}`, { owner_user_id: newOwnerUserId });
      toast.success("Project owner changed");
      setShowOwnerModal(false);
      fetchProject();
      fetchMembers();
      notifyProjectChanged();
    } catch (err) {
      console.error("Change owner error:", err);
      toast.error(err.response?.data?.message || "Failed to change project owner");
    }
  };

  if (loadingProject && !project) {
    return (
      <div className="unit-loader">
        <Spinner animation="border" />
        <p>Loading project...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="unit-card p-4">
        <h5>Unable to load project</h5>
        <p className="text-muted">{loadError}</p>
        {onCollapse && (
          <Button className="unit-btn unit-btn-light" onClick={onCollapse}>
            <i className="fa-solid fa-chevron-up" aria-hidden="true"></i>
            Collapse
          </Button>
        )}
      </div>
    );
  }

  if (!project) return null;

  return (
    <>
      {onCollapse && (
        <div className="projdetail-inline-summary">
          <div className="projdetail-inline-summary-title">
            <i className="fa-solid fa-chevron-down" aria-hidden="true"></i>
            <span className="projdetail-inline-summary-label">Project Workspace</span>
          </div>
          <div className="projdetail-inline-summary-grid">
            <span><strong className="projdetail-inline-summary-name">{project.name}</strong></span>
            <span><Badge bg={statusColor(project.status)}>{project.status}</Badge></span>
            <span>Code: <strong>{project.code}</strong></span>
            <span>Owner: <strong>{project.owner_name || "Unassigned"}</strong></span>
            <span>Members: <strong>{project.active_member_count ?? 0}</strong></span>
            <span>Modules: <strong>{project.active_module_count ?? 0}</strong></span>
            <span>Active: <strong>{project.is_active ? "Yes" : "No"}</strong></span>
          </div>
        </div>
      )}

      <Tabs activeKey={activeTab} onSelect={handleSelectTab} className="projdetail-tabs mb-3">
        <Tab eventKey="overview" title="Overview">
          <div className="unit-card p-4">
            <div className="row g-3 projdetail-overview-grid">
              <div className="col-md-4"><strong>Name:</strong> {project.name}</div>
              <div className="col-md-4"><strong>Code:</strong> {project.code}</div>
              <div className="col-md-4"><strong>Status:</strong> <Badge bg={statusColor(project.status)}>{project.status}</Badge></div>
              <div className="col-md-8"><strong>Description:</strong> {project.description || "No description"}</div>
              <div className="col-md-4"><strong>Owner:</strong> {project.owner_name || "—"}</div>
              <div className="col-md-4"><strong>Department:</strong> {project.department_name || "—"}</div>
              <div className="col-md-4"><strong>Unit:</strong> {project.unit_name || "—"}</div>
              <div className="col-md-4"><strong>Start Date:</strong> {project.start_date ? String(project.start_date).slice(0, 10) : "—"}</div>
              <div className="col-md-4"><strong>End Date:</strong> {project.end_date ? String(project.end_date).slice(0, 10) : "—"}</div>
              <div className="col-md-4"><strong>Active:</strong> {project.is_active ? "Yes" : "No"}</div>
              <div className="col-md-4"><strong>Member Count:</strong> {project.active_member_count ?? 0}</div>
              <div className="col-md-4"><strong>Module Count:</strong> {project.active_module_count ?? 0}</div>
              <div className="col-md-6"><strong>Created:</strong> {formatDateTime(project.created_at)}{project.created_by ? ` by ${resolveUserName(project.created_by)}` : ""}</div>
              <div className="col-md-6"><strong>Updated:</strong> {formatDateTime(project.updated_at)}{project.updated_by ? ` by ${resolveUserName(project.updated_by)}` : ""}</div>
            </div>

            {/* Change Project Owner moves to the persistent footer below when
                this panel is embedded inline (onCollapse provided), matching
                the list's expand-row layout. On the standalone page (no
                onCollapse) it stays here, inside Overview, as before. */}
            {!onCollapse && (
              <Can moduleKey="projects" actionKey="change_owner">
                <Button className="unit-btn unit-btn-light mt-3" onClick={openOwnerModal}>
                  <i className="fa-solid fa-right-left" aria-hidden="true"></i>
                  Change Project Owner
                </Button>
              </Can>
            )}
          </div>
        </Tab>

        <Tab eventKey="members" title="Members">
          <div className="unit-card p-4">
            <Can moduleKey="project_members" actionKey="add">
              <div className="d-flex gap-2 mb-2 projdetail-add-row" style={{ flexWrap: "wrap" }}>
                <div className="projdetail-picker-wrap" style={{ flex: 1, minWidth: 260 }}>
                  <ProjectMemberPicker
                    users={allUsers}
                    excludeUserIds={activeMemberUserIds}
                    value={newMemberUserId}
                    onChange={setNewMemberUserId}
                    placeholder="Search active users to add..."
                  />
                </div>
                <Form.Select
                  className="projdetail-type-select"
                  style={{ maxWidth: 160 }}
                  value={newMemberType}
                  onChange={(e) => setNewMemberType(e.target.value)}
                >
                  {MEMBERSHIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </Form.Select>
                <Button className="unit-btn unit-btn-primary" onClick={addMember} disabled={!newMemberUserId}>
                  <i className="fa-solid fa-user-plus" aria-hidden="true"></i>
                  Add Member
                </Button>
              </div>
              <div className="d-flex gap-3 mb-3 flex-wrap projdetail-add-flags">
                <Form.Check
                  type="checkbox"
                  label="Is Project Manager"
                  checked={newMemberIsManager}
                  onChange={(e) => setNewMemberIsManager(e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  label="Can Manage Members"
                  checked={newMemberCanManageMembers}
                  onChange={(e) => setNewMemberCanManageMembers(e.target.checked)}
                />
                <Form.Check
                  type="checkbox"
                  label="Can Manage Modules"
                  checked={newMemberCanManageModules}
                  onChange={(e) => setNewMemberCanManageModules(e.target.checked)}
                />
              </div>
            </Can>

            {loadingMembers ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <Table responsive className="unit-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Department</th>
                    <th>Role</th>
                    <th>Membership Type</th>
                    <th>Project Manager</th>
                    <th>Can Manage Members</th>
                    <th>Can Manage Modules</th>
                    <th>Joined At</th>
                    <th>Active</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {membersWithUserInfo.map((m) => {
                    const canEditFlags = can("project_members", "edit") || can("project_members", "manage");
                    return (
                      <tr key={m.id}>
                        <td>{m.user_name}{m.employee_code ? <span className="text-muted"> ({m.employee_code})</span> : null}</td>
                        <td>{m.email || "—"}</td>
                        <td>{m.department_name || "—"}</td>
                        <td>{m.role_name || "—"}</td>
                        <td>
                          <Form.Select
                            size="sm"
                            className="projdetail-role-select"
                            value={m.membership_type}
                            style={{ minWidth: 120 }}
                            disabled={!canEditFlags}
                            onChange={(e) => updateMembershipType(m, e.target.value)}
                          >
                            {MEMBERSHIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          </Form.Select>
                        </td>
                        <td>
                          <Badge
                            bg={m.is_project_manager ? "info" : "light"}
                            text={m.is_project_manager ? undefined : "dark"}
                            style={canEditFlags ? { cursor: "pointer" } : undefined}
                            onClick={canEditFlags ? () => toggleMembershipFlag(m, "is_project_manager") : undefined}
                            title={canEditFlags ? "Click to toggle" : undefined}
                          >
                            {m.is_project_manager ? "Yes" : "No"}
                          </Badge>
                        </td>
                        <td>
                          <Badge
                            bg={m.can_manage_members ? "info" : "light"}
                            text={m.can_manage_members ? undefined : "dark"}
                            style={canEditFlags ? { cursor: "pointer" } : undefined}
                            onClick={canEditFlags ? () => toggleMembershipFlag(m, "can_manage_members") : undefined}
                            title={canEditFlags ? "Click to toggle" : undefined}
                          >
                            {m.can_manage_members ? "Yes" : "No"}
                          </Badge>
                        </td>
                        <td>
                          <Badge
                            bg={m.can_manage_modules ? "info" : "light"}
                            text={m.can_manage_modules ? undefined : "dark"}
                            style={canEditFlags ? { cursor: "pointer" } : undefined}
                            onClick={canEditFlags ? () => toggleMembershipFlag(m, "can_manage_modules") : undefined}
                            title={canEditFlags ? "Click to toggle" : undefined}
                          >
                            {m.can_manage_modules ? "Yes" : "No"}
                          </Badge>
                        </td>
                        <td>{formatDateTime(m.joined_at)}</td>
                        <td><Badge bg={m.is_active ? "success" : "secondary"}>{m.is_active ? "Active" : "Inactive"}</Badge></td>
                        <td>
                          <div className="unit-actions">
                            <Button
                              className="unit-icon-btn projdetail-toggle"
                              title={m.is_active ? "Deactivate" : "Reactivate"}
                              aria-label={m.is_active ? `Deactivate ${m.user_name || "member"}` : `Reactivate ${m.user_name || "member"}`}
                              onClick={() => toggleMembershipStatus(m)}
                            >
                              <i className={`fa-solid ${m.is_active ? "fa-pause" : "fa-play"}`} aria-hidden="true"></i>
                            </Button>
                            <Can moduleKey="project_members" actionKey="remove">
                              <Button
                                className="unit-icon-btn unit-delete"
                                title="Remove"
                                aria-label={`Remove ${m.user_name || "member"}`}
                                onClick={() => setRemoveMemberTarget(m)}
                              >
                                <i className="fa-solid fa-user-minus" aria-hidden="true"></i>
                              </Button>
                            </Can>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {membersWithUserInfo.length === 0 && (
                    <tr>
                      <td colSpan="11">
                        <div className="unit-empty">
                          <div className="unit-empty-icon">
                            <i className="fa-solid fa-users" aria-hidden="true"></i>
                          </div>
                          <h5>No project members found.</h5>
                          <p>Add the first project member above.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </div>
        </Tab>

        <Tab eventKey="modules" title="Modules">
          <div className="unit-card p-4">
            <ProjectModuleManagement
              projectId={projectId}
              projectMembers={activeMembers}
              onChanged={() => {
                fetchProject();
                notifyProjectChanged();
              }}
            />
          </div>
        </Tab>

        <Tab eventKey="module-members" title="Module Members">
          <div className="unit-card p-4">
            <ModuleMembersManagement
              projectId={projectId}
              projectMembers={activeMembers}
            />
          </div>
        </Tab>
      </Tabs>

      {onCollapse && (
        <div className="projdetail-inline-footer">
          <Can moduleKey="projects" actionKey="change_owner">
            <Button className="unit-btn unit-btn-light" onClick={openOwnerModal}>
              <i className="fa-solid fa-right-left" aria-hidden="true"></i>
              Change Project Owner
            </Button>
          </Can>

          <Button className="unit-btn unit-btn-light" onClick={onCollapse}>
            <i className="fa-solid fa-chevron-up" aria-hidden="true"></i>
            Collapse
          </Button>
        </div>
      )}

      <Modal
        show={showOwnerModal}
        onHide={() => setShowOwnerModal(false)}
        centered
        className="unit-modal"
        dialogClassName="admin-teal-modal"
        backdropClassName="admin-teal-backdrop"
      >
        <Modal.Header closeButton closeLabel="Close">
          <div className="d-flex align-items-center">
            <SwapHorizOutlinedIcon className="modal-header-icon" aria-hidden="true" />
            <Modal.Title>Change Project Owner</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            The new owner will be given an active OWNER membership on this project.
            The previous owner's membership, if any, is preserved unchanged.
          </p>
          <ProjectMemberPicker
            users={allUsers}
            value={newOwnerUserId}
            onChange={setNewOwnerUserId}
            placeholder="Select new owner..."
          />
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={() => setShowOwnerModal(false)}>Cancel</Button>
          <Button className="unit-btn unit-btn-primary" onClick={changeOwner}>Change Owner</Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={!!removeMemberTarget}
        onHide={() => setRemoveMemberTarget(null)}
        onConfirm={confirmRemoveMember}
        busy={removingMember}
        title="Remove Project Member?"
        message={`Remove ${removeMemberTarget?.user_name} from this project? Their membership history is preserved.`}
        confirmLabel="Remove"
        dialogClassName="admin-teal-modal admin-teal-modal-sm admin-teal-modal-danger"
      />
    </>
  );
}
